import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Workflow } from "lucide-react";

const L = (xs) => (xs || []).map((x) => (typeof x === "string" ? x : x.task || x.requirement)).join(", ") || "None";

function Recon({ rec, id }) {
  return (
    <details className="mt-1" data-testid={`pm-recon-${id}`}>
      <summary className="cursor-pointer text-[#006090]">Reconciliation preview</summary>
      <ul className="mt-1 space-y-0.5 text-slate-600">
        <li><b>Current:</b> {rec.current.program} · version {rec.current.version_label || rec.current.version_id}</li>
        <li><b>Intended:</b> {rec.intended.program} · version {rec.intended.version_label || rec.intended.version_id} · {rec.intended.requirements} requirements</li>
        <li><b>Milestones carried:</b> {L(rec.milestones_carried)}</li>
        <li><b>Tasks carried:</b> {L(rec.tasks_carried)}</li>
        <li><b>Partial (evidence kept, work remains):</b> {L(rec.tasks_partial)}</li>
        <li><b>New target tasks:</b> {rec.tasks_to_assign.filter((t) => t.when === "active now").length} active now · {rec.tasks_to_assign.filter((t) => t.when !== "active now").length} staged for later phases</li>
        <li><b>Retired from active list:</b> {rec.tasks_retired_from_active_list.length} · completed tasks kept as history: {rec.completed_tasks_kept_as_history.length}</li>
        <li><b>Documents retained:</b> {rec.documents_retained}</li>
        <li className="text-amber-700"><b>Needs review:</b> {L(rec.milestones_review)}</li>
      </ul>
    </details>
  );
}

export default function ProgramMismatch() {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (mode) => {
    if (mode === "apply" && !window.confirm("Move these candidates to the program their Candidate Type shows? Completed work is kept; only equivalent requirements carry forward.")) return;
    setBusy(true);
    try { const r = (await api.post(`/programs/program-mismatches/${mode}`)).data; setRep(r); if (mode === "apply") toast.success("Program mismatches corrected"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const fixable = (rep?.rows || []).filter((r) => r.action === "transition").length;
  return (
    <div className="space-y-5 max-w-5xl" data-testid="program-mismatch-page">
      <div><h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Workflow className="w-6 h-6 text-[#006090]" />Program Mismatch Repair</h1>
        <p className="text-sm text-slate-500 mt-1">Finds candidates whose Candidate Type shows one program but whose active enrollment is still another (for example, changed from Protégé to Associate Agent before this fix). Check first, then apply.</p></div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => run("preview")} disabled={busy} data-testid="pm-preview">Check</Button>
        {rep && !rep.applied && <Button onClick={() => run("apply")} disabled={busy || !fixable} className="bg-[#006090] hover:bg-[#004E75]" data-testid="pm-apply">Correct {fixable}</Button>}
      </div>
      {rep && !rep.rows.length && <p className="text-sm text-slate-600" data-testid="pm-none">No mismatches found.</p>}
      {rep?.rows?.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="pm-report">
          <table className="w-full text-sm"><thead className="bg-slate-50 text-xs text-slate-500"><tr>{["Person", "Enrolled in", "Candidate Type says", "Result"].map((h) => <th key={h} className="text-left p-2 font-semibold">{h}</th>)}</tr></thead>
            <tbody>{rep.rows.map((r) => (
              <tr key={r.enrollment_id} className="border-t border-slate-100" data-testid={`pm-row-${r.person_number}`}>
                <td className="p-2">{r.person} <span className="text-xs text-slate-400">{r.person_number}</span></td>
                <td className="p-2">{r.enrolled_program}</td><td className="p-2">{r.candidate_type_program}</td>
                <td className="p-2 text-xs">{r.result || (r.action === "review" ? r.note : "Will move to new program")}
                  {r.carried && <p className="text-slate-500">{r.carried.length} carried forward · {r.tasks_superseded} old tasks superseded</p>}
                  {r.review?.length > 0 && <p className="text-amber-700">Review: {r.review.map((x) => x.requirement).join(", ")}</p>}
                  {r.reconciliation && <Recon rec={r.reconciliation} id={r.person_number} />}</td>
              </tr>))}</tbody></table>
        </div>)}
    </div>
  );
}
